/**
 * Recovery from stale code-split chunks after a deploy.
 *
 * Each frontend revision ships only its own hashed chunks. A tab whose `index.html` predates the
 * deploy asks for chunks that now 404, and the lazy import rejects. A full reload fetches the new
 * `index.html` (served `no-cache`, see `apps/frontend/nginx.conf`) and the new chunk names.
 */

const RELOAD_STAMP_KEY = 'last-chunk-error-reload';

/** One automatic reload per window: if the fresh page still fails, show the error instead of looping. */
const MIN_RELOAD_INTERVAL_MS = 10_000;

/**
 * Each engine words a failed dynamic import differently:
 * - Chrome / Edge: "Failed to fetch dynamically imported module: <url>"
 * - Firefox: "error loading dynamically imported module: <url>"
 * - Safari: "Importing a module script failed."
 * - Vite CSS preload: "Unable to preload CSS for <url>"
 * - webpack-style: "Loading chunk <n> failed"
 */
const CHUNK_LOAD_ERROR_PATTERNS = [
  /dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
  /loading (css )?chunk/i,
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

let reloadRequested = false;

/**
 * Reloads the page unless it already did so within {@link MIN_RELOAD_INTERVAL_MS}. Returns true
 * while a reload is underway (requested now or earlier in this page's life), so callers can skip
 * reporting the error.
 */
export function reloadForStaleChunk(): boolean {
  if (reloadRequested) return true;
  const now = Date.now();
  try {
    const last = Number.parseInt(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? '', 10);
    if (Number.isFinite(last) && now - last <= MIN_RELOAD_INTERVAL_MS) return false;
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
  } catch {
    // Storage unavailable: reload once and accept the (unguarded) risk of a second attempt.
  }
  reloadRequested = true;
  window.location.reload();
  return true;
}

/**
 * Vite dispatches `vite:preloadError` on `window` whenever a dynamic import or its preloads fail,
 * including imports outside React's render. Reloading there recovers before an error boundary has
 * to show anything; the boundary stays as the fallback.
 */
export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', () => {
    reloadForStaleChunk();
  });
}

/** Test-only: resets the per-page "reload already requested" flag. */
export function resetStaleChunkReloadForTests(): void {
  reloadRequested = false;
}
